import { useState } from "react";
import { checkRepository, type RepositoryCheck } from "../../../api/externalConnectionsApi";
import type { ImportBlocker } from "./ImportSubmit";

/**
 * What a Cloud import of a repository needs from the person (ADR-0035): nothing for a public
 * one; for a private one, their connection to its host, DMW's App installed on it, and their
 * consent to copying it for the project's members. Asked of the server when the address is
 * left and again before importing; the server checks it once more when it binds.
 */
export interface RepositoryStep {
  /** The check of the address as it is now; `null` before one. */
  readonly current: RepositoryCheck | null;
  /** Consent belongs to one address: changing the address asks again. */
  readonly consented: boolean;
  readonly setConsented: (consented: boolean) => void;
  /** Ask the server; `fresh` asks again even for the address checked last, as importing does,
   * since installing the App or connecting in another tab changes the answer. */
  readonly check: (remote: string, options?: { readonly fresh: boolean }) => Promise<RepositoryCheck>;
}

export function useRepositoryStep(remote: string): RepositoryStep {
  const [checked, setChecked] = useState<{ readonly remote: string; readonly result: RepositoryCheck } | null>(null);
  const [consentedRemote, setConsentedRemote] = useState<string | null>(null);
  return {
    current: checked !== null && checked.remote === remote ? checked.result : null,
    consented: consentedRemote === remote,
    setConsented: (consented) => { setConsentedRemote(consented ? remote : null); },
    check: async (address, options) => {
      if (options?.fresh !== true && checked !== null && checked.remote === address) return checked.result;
      const result = await checkRepository(address);
      setChecked({ remote: address, result });
      return result;
    },
  };
}

/** The import's reason to wait for a repository's check, if any; `null` once it may go ahead. */
export function repositoryBlocker(check: RepositoryCheck | null, consented: boolean): ImportBlocker | null {
  switch (check?.outcome) {
    case "connect":
    case "reconnect":
      return "connectRepository";
    case "not_visible":
      return "installApp";
    case "ready":
      return consented ? null : "consentPrivate";
    case "public":
    case "not_configured":
    case undefined:
      return null;
  }
}

/** The connection to import with: only for a private repository the person may read. */
export function importConnection(check: RepositoryCheck): string | null {
  return check.outcome === "ready" ? check.connectionId : null;
}
